/**
 * FILE-OVERVIEW:
 * - 役割: 「その他」画面。外部アプリが自動投入した素材をジャンル別タブで一覧表示する。
 * - 主な連携: services/externalFeedService.ts, Common/AppHeader.tsx
 * - 本番運用上の注意: 読み取り専用。ジャンルの追加は EXTERNAL_FEED_GENRES に定義を足すだけでタブが増える。
 *   外部アプリは本番の出稿バケットにしか書かないため、開発環境では一覧が空になる。
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, IconButton, Paper, Stack, Tab, Tabs,
  ThemeProvider, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AppHeader from '../Common/AppHeader';
import { theme } from '../../utils/colors';
import {
  EXTERNAL_FEED_GENRES, EXTERNAL_FEED_RECENT_DAYS, listExternalFeedItems,
} from '../../services/externalFeedService';
import type { ExternalFeedItem } from '../../services/externalFeedService';
import { asErrorLike } from '../../utils/errorLike';

interface OtherPageProps {
  /** App から渡されるが本コンポーネントでは未使用 */
  user?: unknown;
  /** 同上 */
  isDesk?: boolean;
  /** 同上 */
  onSignOut?: () => void;
}

const formatReceivedAt = (date: Date): string => date.toLocaleString('ja-JP', {
  timeZone: 'Asia/Tokyo',
  month: 'numeric',
  day: 'numeric',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

// ヘッダーの「その他」ボタンと同じ色
const ACCENT_COLOR = '#e37400';

const FeedItemCard = ({ item }: { item: ExternalFeedItem }) => (
  <Paper
    elevation={0}
    sx={{
      // App.css の .App が中央寄せのため、ここで左寄せに戻す
      textAlign: 'left',
      bgcolor: '#ffffff',
      border: '1px solid #b8c2cf',
      borderLeft: `5px solid ${ACCENT_COLOR}`,
      borderRadius: 1.5,
      boxShadow: '0 1px 3px rgba(60,64,67,0.18)',
      overflow: 'hidden',
    }}
  >
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ px: 2, py: 1, bgcolor: '#fef3e6', borderBottom: '1px solid #f3d3ad' }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color: '#7a3e00', fontVariantNumeric: 'tabular-nums' }}
      >
        {formatReceivedAt(item.receivedAt)}
      </Typography>
      {item.revision >= 3 && <Chip size="small" color="warning" label={`訂正（版${item.revision - 1}）`} />}
      {item.isTest && <Chip size="small" variant="outlined" label="テスト" />}
    </Stack>
    <Box sx={{ px: 2, py: 1.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#1f1f1f', mb: 1 }}>
        {item.headline || '（見出しなし）'}
      </Typography>
      <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.9, fontSize: '0.95rem', color: '#202124' }}>
        {item.body}
      </Typography>
      {item.sourceNote && (
        <Typography
          variant="body2"
          component="p"
          sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed #c7d0db', color: '#5f6368' }}
        >
          {item.sourceNote}
        </Typography>
      )}
    </Box>
  </Paper>
);

const OtherPage = (_props: OtherPageProps) => {
  const isMobile = useMediaQuery('(max-width:1023px)');
  const [activeGenre, setActiveGenre] = useState(EXTERNAL_FEED_GENRES[0].key);
  const [itemsByGenre, setItemsByGenre] = useState<Record<string, ExternalFeedItem[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItemsByGenre(await listExternalFeedItems());
    } catch (err) {
      setError(asErrorLike(err).message || '一覧の取得に失敗しました');
      console.error('外部投入素材の取得エラー:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const items = itemsByGenre[activeGenre] || [];

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          maxWidth: 1920,
          width: '100%',
          mx: 'auto',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
        }}
      >
        <AppHeader />

        <Tabs
          value={activeGenre}
          onChange={(_e, next: string) => setActiveGenre(next)}
          sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 44, px: 1, bgcolor: 'background.paper' }}
        >
          {EXTERNAL_FEED_GENRES.map((genre) => (
            <Tab key={genre.key} value={genre.key} label={genre.label} sx={{ minHeight: 44 }} />
          ))}
        </Tabs>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: isMobile ? 1.5 : 3, py: 2, bgcolor: '#eef1f5' }}>
          <Box sx={{ maxWidth: 960, mx: 'auto' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: '#3c4043', flex: 1, textAlign: 'left' }}>
                直近{EXTERNAL_FEED_RECENT_DAYS}日間に外部アプリから取り込まれた素材（新しい順）
              </Typography>
              <Tooltip title="再読み込み">
                <span>
                  <IconButton onClick={() => void fetchItems()} disabled={loading} size="small">
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : items.length === 0 ? (
              !error && (
                <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 6 }}>
                  取り込まれた素材はありません
                </Typography>
              )
            ) : (
              <Stack spacing={1.5}>
                {items.map((item) => <FeedItemCard key={item.key} item={item} />)}
              </Stack>
            )}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default OtherPage;
